import { MediaResource } from '@brightmls/nextgen.lae.flc';
import {
  dataURItoBlob,
  downscaleImage,
  getExifInfo,
  ImageConfig,
  ImageUtils,
} from '../../media/utils/imageUtils';
import { EditState, ProcessedImageData, VisibleCarouselImage } from './types';
import {
  DEFAULT_BRIGHTNESS,
  DEFAULT_CONTRAST,
  DEFAULT_SATURATION,
  JPEG_QUALITY,
  CAROUSEL_WINDOW_SIZE,
} from './constants';

export interface CanvasEditState {
  brightness: number;
  contrast: number;
  saturation: number;
  rotation: number;
  isSquare: boolean;
}

export const initializeCanvasWithEdit = (
  canvas: HTMLCanvasElement,
  img: HTMLImageElement,
  edit: CanvasEditState,
) => {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  if (edit.isSquare) {
    const size = Math.min(img.naturalWidth, img.naturalHeight);
    canvas.width = size;
    canvas.height = size;
  } else {
    canvas.width = img.naturalWidth;
    canvas.height = img.naturalHeight;
  }

  const isVerticalRotation = [90, 270].includes(Math.abs(edit.rotation));
  if (edit.rotation !== 0 && isVerticalRotation) {
    canvas.width = img.naturalHeight;
    canvas.height = img.naturalWidth;
  }

  const x = canvas.width / 2;
  const y = canvas.height / 2;
  const width = img.naturalWidth;
  const height = img.naturalHeight;

  ctx.filter = `brightness(${edit.brightness}%) contrast(${edit.contrast}%) saturate(${edit.saturation}%)`;
  ctx.translate(x, y);
  ctx.rotate((edit.rotation * Math.PI) / 180);
  ctx.drawImage(img, -width / 2, -height / 2, width, height);
  ctx.rotate((-1 * edit.rotation * Math.PI) / 180);
  ctx.translate(-x, -y);
};

export const blobToFile = (blob: Blob, filename: string): File => {
  return new File([blob], filename, {
    type: blob.type,
    lastModified: Date.now(),
  });
};

/**
 * Check if an image has been edited from its default state
 */
export const isImageEdited = (editState: EditState): boolean => {
  return (
    editState.brightness !== DEFAULT_BRIGHTNESS ||
    editState.contrast !== DEFAULT_CONTRAST ||
    editState.saturation !== DEFAULT_SATURATION ||
    editState.rotation !== 0 ||
    editState.crop !== null ||
    editState.isSquare !== false ||
    editState.sourceOverride !== null
  );
};

/**
 * Render a photo off-screen for processing
 */
export const renderPhotoOffscreen = async (
  mediaRecord: MediaResource,
  editState: EditState,
): Promise<{ off: HTMLCanvasElement; img: HTMLImageElement } | null> => {
  if (!mediaRecord) return null;

  // Load the image
  const img = new Image();
  img.crossOrigin = 'anonymous';
  const override = editState?.sourceOverride;
  img.src = (override || (mediaRecord.MEDIA_URL as string)) as string;
  await new Promise<void>((resolve, reject) => {
    img.onload = () => resolve();
    img.onerror = reject;
  });

  // Render to an offscreen canvas
  const off = document.createElement('canvas');
  initializeCanvasWithEdit(off, img, editState);

  return { off, img };
};

/**
 * Get processed image data for a specific media record and edit state
 */
export const getProcessedImageData = async (
  mediaRecord: MediaResource,
  editState: EditState,
): Promise<ProcessedImageData | null> => {
  if (!isImageEdited(editState)) return null;

  const rendered = await renderPhotoOffscreen(mediaRecord, editState);
  if (!rendered) return null;

  const { off } = rendered;

  // 1) Get the rendered image data URL
  const dataURL = off.toDataURL('image/jpeg', JPEG_QUALITY);

  // 2) Download the current file from MEDIA_URL to memory and extract EXIF
  let finalBlob: Blob | null = null;
  let finalFile: File | null = null;
  try {
    const response = await fetch(mediaRecord.MEDIA_URL as string, {
      credentials: 'omit',
    });
    const originalBlob = await response.blob();
    const originalFile = new File(
      [originalBlob],
      mediaRecord.MEDIA_SOURCE_FILE_NAME || 'image.jpg',
      { type: originalBlob.type || 'image/jpeg' },
    );

    const resizeImgConfig = {
      file: originalFile,
      targetSize: {
        width: ImageUtils.TARGET_WIDTH,
        height: ImageUtils.TARGET_HEIGHT,
      },
    } as const;

    const exifResponse = await getExifInfo(resizeImgConfig);

    // 3) Extract base64 portion from rendered dataURL
    const base64Data = dataURL.split(',')[1];

    // 4) Downscale while preserving EXIF
    const imageConfig: ImageConfig = {
      maxWidth: ImageUtils.TARGET_WIDTH,
      maxHeight: ImageUtils.TARGET_HEIGHT,
      quality: ImageUtils.JPEG_COMPRESSION,
    };
    const result = await downscaleImage(base64Data, imageConfig, exifResponse);

    // 5) Create blob from the downscale results
    finalBlob = dataURItoBlob(result.base64Data);
    finalFile = blobToFile(
      finalBlob,
      mediaRecord.MEDIA_SOURCE_FILE_NAME || 'image.jpg',
    );

    // Prefer the EXIF-preserved data URL for downstream preview/usage
    return {
      blob: finalBlob,
      dataURL: result.base64Data,
      file: finalFile,
      mediaRecord,
    };
  } catch {
    // If anything fails, fall back to the rendered output
    const fallbackBlob = await new Promise<Blob | null>((resolve) =>
      off.toBlob(resolve, 'image/jpeg', JPEG_QUALITY),
    );
    const fallbackFile = fallbackBlob
      ? blobToFile(
          fallbackBlob,
          mediaRecord.MEDIA_SOURCE_FILE_NAME || 'image.jpg',
        )
      : null;
    return { blob: fallbackBlob, dataURL, file: fallbackFile, mediaRecord };
  }
};

/**
 * Get the images that should be visible in the carousel viewport
 */
export const getVisibleCarouselImages = (
  photoMedia: MediaResource[],
  carouselViewportIndex: number,
  limit: number = CAROUSEL_WINDOW_SIZE,
): VisibleCarouselImage[] => {
  const totalImages = photoMedia.length;
  const previousImages: VisibleCarouselImage[] = [];
  const nextImages: VisibleCarouselImage[] = [];
  const carouselLimit = limit - 1;
  const prevImagesLimit = Math.floor(carouselLimit / 2);
  const nextImagesLimit = Math.ceil(carouselLimit / 2);

  // If we have 9 or fewer images, show all
  if (totalImages <= limit) {
    return photoMedia.map((photo, index) => ({
      photo,
      index,
      relativePosition: index - carouselViewportIndex,
    }));
  }

  // Get the previous images
  for (let i = prevImagesLimit; i >= 1; i--) {
    const prevIndex = (carouselViewportIndex - i + totalImages) % totalImages;
    previousImages.push({
      photo: photoMedia[prevIndex],
      index: prevIndex,
      relativePosition: i - 4,
    });
  }

  // Get the next images
  for (let i = 1; i <= nextImagesLimit; i++) {
    const nextIndex = (carouselViewportIndex + i) % totalImages;
    nextImages.push({
      photo: photoMedia[nextIndex],
      index: nextIndex,
      relativePosition: i,
    });
  }

  const visibleImages = [
    ...previousImages,
    {
      photo: photoMedia[carouselViewportIndex],
      index: carouselViewportIndex,
      relativePosition: 0,
    },
    ...nextImages,
  ];

  return visibleImages;
};

/**
 * Transform drag deltas based on image rotation to correctly handle user input
 * when the image is rotated. This ensures that dragging left/right or up/down
 * behaves correctly relative to the visually displayed (rotated) image.
 *
 * @param dx - Original horizontal drag delta
 * @param dy - Original vertical drag delta
 * @param rotation - Current rotation angle in degrees
 * @returns Transformed drag deltas accounting for rotation
 */
export const transformDragDeltaForRotation = (
  dx: number,
  dy: number,
  rotation: number,
): { dx: number; dy: number } => {
  // Normalize rotation to 0-360 range
  const normalizedRotation = ((rotation % 360) + 360) % 360;

  switch (normalizedRotation) {
    case 0:
      // No rotation - no transformation needed
      return { dx, dy };
    case 90:
      // 90° clockwise - inverse of what I had before
      return { dx: dy, dy: -dx };
    case 180:
      // 180° rotation - inverse of what I had before
      return { dx: -dx, dy: -dy };
    case 270:
      // 270° clockwise - inverse of what I had before
      return { dx: -dy, dy: dx };
    default:
      // For any other rotation, fall back to no transformation
      return { dx, dy };
  }
};
